// AIDEV-NOTE: Indicador de sincronizacao da AppBar — ultimo passo do fix de perda de pedido
// (2026-10). O vendedor fecha o pedido sem sinal e precisa de um sinal permanente de que ele
// ainda esta so no aparelho; sem isso ele nao sabe se pode confiar e acha que perdeu o trabalho.
// Fica INVISIVEL quando esta tudo em dia e online: indicador que vive aceso vira ruido e para de
// ser notado justamente quando importa.

import { useState, type MouseEvent } from 'react';
import {
  Badge,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  Popover,
  Stack,
  Tooltip,
  Typography
} from '@mui/material';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import CloudQueueIcon from '@mui/icons-material/CloudQueue';
import SyncProblemIcon from '@mui/icons-material/SyncProblem';
import { useOfflineSync } from '@/hooks/useOfflineSync';

export function SyncStatusIndicator() {
  const {
    isOnline,
    isSyncing,
    pendingOperations,
    failedOperations,
    ultimoErro,
    lastSyncAt,
    triggerSync,
    reenviarFalhas
  } = useOfflineSync();

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const totalNaFila = pendingOperations + failedOperations;
  const temAlgoAMostrar = !isOnline || totalNaFila > 0 || isSyncing;

  if (!temAlgoAMostrar) return null;

  // AIDEV-NOTE: ordem de precedencia da mensagem — falha (exige acao) > offline (explica a causa)
  // > enviando > aguardando. O rotulo vira o aria-label do botao, entao e tambem o texto que o
  // leitor de tela anuncia.
  const { icone, rotulo, cor } = (() => {
    if (failedOperations > 0) {
      return {
        icone: <SyncProblemIcon />,
        rotulo: `${failedOperations} com falha no envio`,
        cor: 'error' as const
      };
    }
    if (!isOnline) {
      return {
        icone: <CloudOffIcon />,
        rotulo:
          totalNaFila > 0
            ? `Sem conexão — ${totalNaFila} aguardando envio`
            : 'Sem conexão — trabalhando no aparelho',
        cor: 'warning' as const
      };
    }
    if (isSyncing) {
      return { icone: <CloudQueueIcon />, rotulo: 'Enviando pedidos...', cor: 'info' as const };
    }
    return {
      icone: <CloudQueueIcon />,
      rotulo: `${totalNaFila} aguardando envio`,
      cor: 'warning' as const
    };
  })();

  const handleAbrir = (e: MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget);
  const handleFechar = () => setAnchorEl(null);

  return (
    <>
      <Tooltip title={rotulo}>
        {/* color="inherit": herda a cor da AppBar, sem introduzir token novo de paleta */}
        <IconButton color="inherit" aria-label={rotulo} onClick={handleAbrir} size="small">
          {isSyncing ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            <Badge badgeContent={totalNaFila} color={cor} max={99}>
              {icone}
            </Badge>
          )}
        </IconButton>
      </Tooltip>

      <Popover
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={handleFechar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ p: 2, maxWidth: 320 }}>
          <Typography variant="subtitle2" gutterBottom>
            Sincronização
          </Typography>

          <Typography variant="body2" color="text.secondary">
            {rotulo}
          </Typography>

          {totalNaFila > 0 && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
              Os pedidos estão salvos no aparelho e sobem sozinhos quando a conexão voltar.
            </Typography>
          )}

          {/* AIDEV-NOTE: mostra o motivo da falha — uma op 'failed' por erro de negocio (ex.: 400)
              nunca vai passar no reenvio, e o vendedor precisa saber disso em vez de so insistir */}
          {ultimoErro && (
            <Typography
              variant="caption"
              color="error"
              display="block"
              sx={{ mt: 1, wordBreak: 'break-word' }}
            >
              Último erro: {ultimoErro}
            </Typography>
          )}

          {lastSyncAt && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
              Último envio: {lastSyncAt.toLocaleTimeString('pt-BR')}
            </Typography>
          )}

          <Divider sx={{ my: 1.5 }} />

          <Stack direction="row" spacing={1} justifyContent="flex-end">
            {failedOperations > 0 && (
              <Button size="small" color="warning" onClick={() => void reenviarFalhas()}>
                Reenviar
              </Button>
            )}
            <Button
              size="small"
              variant="contained"
              disabled={!isOnline || isSyncing}
              onClick={() => void triggerSync()}
            >
              Enviar agora
            </Button>
          </Stack>
        </Box>
      </Popover>
    </>
  );
}
